'use client';

import { PRODUCT_TIMELINE_STAGES, PRODUCT_CARD_TYPES, getTimelineQuarters, ProductCardTypeConfig } from '@/config/dashboardWorkflows';
import { useDashboardData } from '@/hooks/useDashboardData';
import { useTaskStageUpdate } from '@/hooks/useTaskStageUpdate';
import { useTaskQuarterUpdate } from '@/hooks/useTaskQuarterUpdate';
import { ProductCard, mapToProductCard } from '@/utils/cardMappers';
import { useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import StarsBackground from './StarsBackground';
import TaskModal from './TaskModal';

export default function ProductTimelineDashboard() {
  const queryClient = useQueryClient();
  const { data: tasksData, isLoading, error } = useDashboardData('product');
  const updateStageMutation = useTaskStageUpdate();
  const updateQuarterMutation = useTaskQuarterUpdate();

  const [isTaskModalOpen, setIsTaskModalOpen] = useState(false);
  const [editingTaskId, setEditingTaskId] = useState<number | undefined>(undefined);
  const [taskModalStage, setTaskModalStage] = useState<number | undefined>(undefined);
  const [taskModalCardType, setTaskModalCardType] = useState<number | null>(null);
  const [showAddDropdown, setShowAddDropdown] = useState(false);
  const [selectedCardTypeFilter, setSelectedCardTypeFilter] = useState<number | null>(null);

  const quarters = useMemo(() => getTimelineQuarters(), []);

  // Map API data to ProductCard[]
  const allTasks = useMemo(() => {
    if (!tasksData) return [];
    return tasksData.map(mapToProductCard);
  }, [tasksData]);

  // Filter by card type
  const tasks = useMemo(() => {
    if (selectedCardTypeFilter === null) return allTasks;
    return allTasks.filter((t: ProductCard) => t.cardTypeNum === selectedCardTypeFilter);
  }, [allTasks, selectedCardTypeFilter]);

  // KPIs
  const kpis = useMemo(() => ({
    totalItems: tasks.length,
    planned: tasks.filter((t: ProductCard) => t.stage === 0).length,
    inProgress: tasks.filter((t: ProductCard) => t.stage === 1).length,
    complete: tasks.filter((t: ProductCard) => t.stage === 3).length,
  }), [tasks]);

  // Group tasks into 2D grid: cardTypeId -> quarterKey -> cards
  const gridData = useMemo(() => {
    const grid: Record<number, Record<string, ProductCard[]>> = {};
    PRODUCT_CARD_TYPES.forEach(ct => {
      grid[ct.id] = {};
      quarters.forEach(q => {
        grid[ct.id][q.key] = tasks.filter(
          (t: ProductCard) => t.cardTypeNum === ct.id && t.quarter === q.key
        );
      });
    });
    return grid;
  }, [tasks, quarters]);

  // Determine which swim lanes to show based on filter
  const visibleCardTypes = useMemo(() => {
    if (selectedCardTypeFilter === null) return PRODUCT_CARD_TYPES;
    return PRODUCT_CARD_TYPES.filter(ct => ct.id === selectedCardTypeFilter);
  }, [selectedCardTypeFilter]);

  const handleCardClick = (taskIdNum: number, cardTypeNum: number, stage: number) => {
    setEditingTaskId(taskIdNum);
    setTaskModalStage(stage);
    setTaskModalCardType(cardTypeNum);
    setIsTaskModalOpen(true);
  };

  const handleAddItem = (cardType: number) => {
    setEditingTaskId(undefined);
    setTaskModalCardType(cardType);
    setTaskModalStage(0);
    setIsTaskModalOpen(true);
    setShowAddDropdown(false);
  };

  const handleTaskSaved = () => {
    queryClient.invalidateQueries({ queryKey: ['dashboard', 'product'] });
    setIsTaskModalOpen(false);
    setEditingTaskId(undefined);
    setTaskModalStage(undefined);
    setTaskModalCardType(null);
  };

  const handleDragStart = (e: React.DragEvent, card: ProductCard) => {
    e.dataTransfer.setData('taskId', card.id);
    e.dataTransfer.setData('taskIdNum', card.taskIdNum.toString());
    e.dataTransfer.setData('cardTypeNum', card.cardTypeNum.toString());
    e.dataTransfer.setData('sourceQuarter', card.quarter);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (e: React.DragEvent, targetQuarter: string) => {
    e.preventDefault();
    const taskIdNum = parseInt(e.dataTransfer.getData('taskIdNum'), 10);
    const sourceQuarter = e.dataTransfer.getData('sourceQuarter');

    if (!taskIdNum || isNaN(taskIdNum) || sourceQuarter === targetQuarter) return;

    const task = allTasks.find((t: ProductCard) => t.taskIdNum === taskIdNum);
    if (!task) return;

    updateQuarterMutation.mutate({
      taskId: taskIdNum,
      quarter: targetQuarter,
      existingData: task.dashboardData || {},
    });
  };

  // Get stage display info
  const getStageInfo = (stage: number) => {
    const stageConfig = PRODUCT_TIMELINE_STAGES.find(s => s.stage === stage);
    if (!stageConfig) return { label: 'Planned', color: 'bg-slate-400' };
    const colorMap: Record<string, string> = {
      slate: 'bg-slate-400',
      blue: 'bg-blue-400',
      purple: 'bg-purple-400',
      emerald: 'bg-emerald-400',
    };
    return { label: stageConfig.label, color: colorMap[stageConfig.color] || 'bg-slate-400' };
  };

  return (
    <div className="flex-1 overflow-hidden flex flex-col bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      <StarsBackground glowColor="bg-violet-500/5" />

      {/* Dashboard Header Bar with KPIs */}
      <div className="relative z-50 flex items-center flex-nowrap gap-6 mb-0 p-5 bg-slate-800/40 backdrop-blur-sm border-b border-slate-700/50">
        {/* Dashboard Title */}
        <div className="flex items-center gap-2 flex-shrink-0">
          <span className="text-2xl">🗺️</span>
          <h1 className="text-xl font-semibold text-white">Product Timeline</h1>
        </div>

        {/* Divider */}
        <div className="w-px h-8 bg-slate-600 flex-shrink-0"></div>

        {/* KPIs */}
        <div className="flex items-center gap-6 flex-shrink min-w-0 flex-nowrap">
          <div className="text-center">
            <div className="text-2xl font-bold text-white">{kpis.totalItems}</div>
            <div className="text-xs text-slate-400">Total Items</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-slate-400">{kpis.planned}</div>
            <div className="text-xs text-slate-400">Planned</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-blue-400">{kpis.inProgress}</div>
            <div className="text-xs text-slate-400">In Progress</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-emerald-400">{kpis.complete}</div>
            <div className="text-xs text-slate-400">Complete</div>
          </div>
        </div>

        {/* Right Side Controls */}
        <div className="flex items-center gap-3 ml-auto flex-shrink-0">
          {/* Add Item Dropdown */}
          <div className="relative z-[60]">
            <button
              onClick={() => setShowAddDropdown(!showAddDropdown)}
              className="flex items-center gap-2 bg-violet-600 hover:bg-violet-500 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Add Item
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>

            {showAddDropdown && (
              <>
                <div className="fixed inset-0 z-[55]" onClick={() => setShowAddDropdown(false)} />
                <div className="absolute right-0 top-full mt-2 bg-slate-800 border border-slate-700 rounded-lg shadow-xl z-[60] py-1 min-w-[200px]">
                  {PRODUCT_CARD_TYPES.map(ct => (
                    <button
                      key={ct.id}
                      onClick={() => handleAddItem(ct.id)}
                      className="w-full flex items-center gap-2 px-4 py-2 text-sm text-left hover:bg-slate-700 transition-colors text-white"
                    >
                      {ct.icon} {ct.label}
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Main Content Area with Sidebar */}
      <div className="relative z-10 flex-1 flex overflow-hidden">
        {/* Sidebar - Card Type Filters */}
        <div className="w-56 bg-slate-800/60 backdrop-blur-sm border-r border-slate-700/50 flex flex-col">
          <div className="p-4">
            <h2 className="text-sm font-semibold text-slate-300 mb-3">Item Types</h2>
            <button
              onClick={() => setSelectedCardTypeFilter(null)}
              className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors mb-1 ${
                selectedCardTypeFilter === null
                  ? 'bg-slate-700 text-white'
                  : 'bg-slate-700/50 text-slate-300 hover:bg-slate-700/70'
              }`}
            >
              All Types
            </button>
            {PRODUCT_CARD_TYPES.map(ct => (
              <button
                key={ct.id}
                onClick={() => setSelectedCardTypeFilter(ct.id)}
                className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors mb-1 ${
                  selectedCardTypeFilter === ct.id
                    ? 'bg-slate-700 text-white'
                    : 'bg-slate-700/50 text-slate-300 hover:bg-slate-700/70'
                }`}
              >
                <span>{ct.icon}</span>
                <span>{ct.label}</span>
              </button>
            ))}
          </div>

          {/* Quarter Legend */}
          <div className="p-4 border-t border-slate-700/50 mt-auto">
            <h2 className="text-sm font-semibold text-slate-300 mb-3">Stages</h2>
            {PRODUCT_TIMELINE_STAGES.map(stage => {
              const info = getStageInfo(stage.stage);
              return (
                <div key={stage.stage} className="flex items-center gap-2 mb-2 text-sm text-slate-400">
                  <div className={`w-2.5 h-2.5 rounded-full ${info.color}`} />
                  <span>{stage.label}</span>
                </div>
              );
            })}
          </div>
        </div>

        {/* Timeline Grid */}
        <div className="flex-1 overflow-auto p-4">
          {isLoading ? (
            <div className="flex items-center justify-center h-full">
              <div className="text-gray-400 text-sm">Loading product timeline...</div>
            </div>
          ) : error ? (
            <div className="flex items-center justify-center h-full">
              <div className="text-red-400 text-sm">{error instanceof Error ? error.message : 'Failed to load tasks'}</div>
            </div>
          ) : (
            <div className="min-w-[800px]">
              {/* Quarter Column Headers */}
              <div className="grid gap-3 mb-3" style={{ gridTemplateColumns: '180px repeat(4, 1fr)' }}>
                <div /> {/* Empty corner cell */}
                {quarters.map(q => (
                  <div
                    key={q.key}
                    className={`text-center py-2.5 rounded-lg font-semibold text-sm ${
                      q.isCurrent
                        ? 'bg-violet-600/30 text-violet-200 border border-violet-500/40'
                        : 'bg-slate-800/60 text-slate-300 border border-slate-700/40'
                    }`}
                  >
                    {q.label}
                    {q.isCurrent && (
                      <span className="ml-2 text-xs font-normal text-violet-400">Current</span>
                    )}
                  </div>
                ))}
              </div>

              {/* Swim Lane Rows */}
              {visibleCardTypes.map(cardType => (
                <SwimLaneRow
                  key={cardType.id}
                  cardType={cardType}
                  quarters={quarters}
                  cards={gridData[cardType.id] || {}}
                  onCardClick={handleCardClick}
                  onDragStart={handleDragStart}
                  onDragOver={handleDragOver}
                  onDrop={handleDrop}
                  getStageInfo={getStageInfo}
                />
              ))}

              {/* Empty state */}
              {tasks.length === 0 && (
                <div className="flex flex-col items-center justify-center py-16 text-slate-500">
                  <span className="text-4xl mb-3">🗺️</span>
                  <p className="text-sm">No items on the timeline yet</p>
                  <p className="text-xs mt-1">Click &quot;Add Item&quot; to get started</p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Task Modal */}
      <TaskModal
        isOpen={isTaskModalOpen}
        onClose={() => {
          setIsTaskModalOpen(false);
          setEditingTaskId(undefined);
          setTaskModalStage(undefined);
          setTaskModalCardType(null);
        }}
        mode={editingTaskId ? 'edit' : 'create'}
        taskId={editingTaskId}
        dashboardType="product"
        dashboardStage={taskModalStage}
        dashboardCardType={taskModalCardType}
        onTaskSaved={handleTaskSaved}
      />
    </div>
  );
}

// Swim Lane Row Component
interface SwimLaneRowProps {
  cardType: ProductCardTypeConfig;
  quarters: { key: string; label: string; isCurrent: boolean }[];
  cards: Record<string, ProductCard[]>;
  onCardClick: (taskIdNum: number, cardTypeNum: number, stage: number) => void;
  onDragStart: (e: React.DragEvent, card: ProductCard) => void;
  onDragOver: (e: React.DragEvent) => void;
  onDrop: (e: React.DragEvent, targetQuarter: string) => void;
  getStageInfo: (stage: number) => { label: string; color: string };
}

function SwimLaneRow({ cardType, quarters, cards, onCardClick, onDragStart, onDragOver, onDrop, getStageInfo }: SwimLaneRowProps) {
  const [dragOverQuarter, setDragOverQuarter] = useState<string | null>(null);

  return (
    <div className="grid gap-3 mb-3" style={{ gridTemplateColumns: '180px repeat(4, 1fr)' }}>
      {/* Row Label */}
      <div className="flex items-center gap-2 px-3 py-3 bg-slate-800/40 rounded-lg border border-slate-700/30">
        <span className="text-lg">{cardType.icon}</span>
        <span className="text-sm font-medium text-slate-200">{cardType.label}</span>
      </div>

      {/* Quarter Cells */}
      {quarters.map(q => {
        const cellCards = cards[q.key] || [];
        const isDragOver = dragOverQuarter === q.key;

        return (
          <div
            key={q.key}
            className={`min-h-[80px] rounded-lg p-2 transition-colors ${
              isDragOver
                ? 'bg-violet-500/20 border-2 border-violet-500/40'
                : 'bg-black/20 border border-slate-700/30'
            }`}
            onDragOver={(e) => {
              onDragOver(e);
              setDragOverQuarter(q.key);
            }}
            onDragLeave={() => setDragOverQuarter(null)}
            onDrop={(e) => {
              setDragOverQuarter(null);
              onDrop(e, q.key);
            }}
          >
            <div className="space-y-2">
              {cellCards.map(card => {
                const stageInfo = getStageInfo(card.stage);
                return (
                  <div
                    key={card.id}
                    draggable
                    onDragStart={(e) => onDragStart(e, card)}
                    onClick={() => onCardClick(card.taskIdNum, card.cardTypeNum, card.stage)}
                    className={`bg-gray-100 rounded-lg p-2.5 border-l-4 ${cardType.borderColor} cursor-pointer hover:bg-gray-200 transition-colors group`}
                  >
                    <div className="flex items-start justify-between gap-1">
                      <h4 className="text-sm font-medium text-gray-800 leading-tight">{card.title}</h4>
                    </div>
                    <div className="flex items-center gap-2 mt-1.5">
                      <div className="flex items-center gap-1">
                        <div className={`w-2 h-2 rounded-full ${stageInfo.color}`} />
                        <span className="text-xs text-gray-500">{stageInfo.label}</span>
                      </div>
                      {card.assignedTo?.full_name && (
                        <>
                          <span className="text-gray-300">·</span>
                          <span className="text-xs text-gray-500 truncate">{card.assignedTo.full_name}</span>
                        </>
                      )}
                    </div>
                    <div className="text-xs text-gray-400 mt-1">{card.taskId}</div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}
